import type { DesignToolProductDetail } from '@/constants';
import type { GfProductSize } from '@/types';
import type { ProductColor } from '../types';

/**
 * 選択された色の追加価格（税込）を取得する
 */
const getColorAdditionalPrice = (colors: ProductColor[], selectedColorId: number): number => {
  const selectedColor = colors.find((color) => color.id === selectedColorId);
  return selectedColor?.additionalTaxIncludedPrice || 0;
};

/**
 * 選択されたサイズの追加価格（税込）を取得する
 */
const getSizeAdditionalPrice = (sizes: GfProductSize[], selectedSizeId: number): number => {
  const selectedSize = sizes.find((size) => size.id === selectedSizeId);
  return selectedSize?.additionalTaxIncludedPrice || 0;
};

/**
 * 商品の表示価格を計算する（税込）
 * basePrice（税込）+ カラー追加料金（税込）+ サイズ追加料金（税込）+ 印刷エリア追加料金（税込）
 */
export const calculateProductDisplayPrice = ({
  basePrice,
  colors = [],
  selectedColorId = 0,
  sizes = [],
  selectedSizeId = 0,
  printAreaAdditionalPrice = 0,
}: {
  basePrice: number;
  colors?: ProductColor[];
  selectedColorId?: number;
  sizes?: GfProductSize[];
  selectedSizeId?: number;
  printAreaAdditionalPrice?: number;
}): number => {
  const colorPrice = getColorAdditionalPrice(colors, selectedColorId);
  const sizePrice = getSizeAdditionalPrice(sizes, selectedSizeId);
  return basePrice + colorPrice + sizePrice + printAreaAdditionalPrice;
};

/**
 * 商品の最低価格を計算する（税込）
 */
export const calculateMinProductPrice = (productDetail: DesignToolProductDetail): number => {
  // カラーの最低追加料金（税込）
  const minColorPrice =
    productDetail.colors.length > 0
      ? Math.min(...productDetail.colors.map((color) => color.additionalTaxIncludedPrice))
      : 0;

  // サイズの最低追加料金（税込）
  const minSizePrice =
    productDetail.sizes.length > 0
      ? Math.min(...productDetail.sizes.map((size) => size.additionalTaxIncludedPrice))
      : 0;

  // 印刷エリアの最低追加料金
  const minPrintAreaPrice =
    productDetail.printAreas.length > 0
      ? Math.min(...productDetail.printAreas.map((area) => area.additionalTaxIncludedPrice))
      : 0;

  return productDetail.basePrice + minColorPrice + minSizePrice + minPrintAreaPrice;
};
